/* Aromante · Hero carrusel
   - Loop infinito: también al deslizar a mano (después del último sigue el primero y viceversa).
   - El avance automático no se detiene con el cursor encima y no se reinicia al volver a la pestaña:
     se pausa y continúa desde donde iba. */
if (!customElements.get('aro-hero-carousel')) {
  class AroHeroCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-dot]'));
      this.n = this.slides.length;
      this.index = 0;
      this.timer = null;
      this.interval = parseInt(this.dataset.interval, 10) || 6000;
      this.remaining = this.interval;
      this.autoplay = this.dataset.autoplay === 'true' && this.n > 1;
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.focused = false;
      this.touching = false;
      this.visible = true;
      this.editorLocked = false;
      this.loop = this.n > 1;
      this.offset = 0;
      this.hasScrollEnd = 'onscrollend' in window;
      this.style.setProperty('--hc-interval', this.interval + 'ms');

      if (this.loop) this.addClones();
      this.linkSlides();

      this.dots.forEach((dot) => dot.addEventListener('click', () => this.goTo(Number(dot.dataset.dot))));
      const prev = this.querySelector('[data-prev]');
      const next = this.querySelector('[data-next]');
      if (prev) prev.addEventListener('click', () => this.goTo(this.index - 1));
      if (next) next.addEventListener('click', () => this.goTo(this.index + 1));

      this.onScroll = this.onScroll.bind(this);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      if (this.hasScrollEnd) this.track.addEventListener('scrollend', () => this.settle());

      /* Solo el foco con teclado pausa (un clic con el mouse no) */
      this.addEventListener('focusin', (e) => { if (e.target.matches(':focus-visible')) { this.focused = true; this.update(); } });
      this.addEventListener('focusout', (e) => { if (!this.contains(e.relatedTarget)) { this.focused = false; this.update(); } });
      this.track.addEventListener('touchstart', () => { this.touching = true; this.update(); }, { passive: true });
      this.track.addEventListener('touchend', () => {
        this.touching = false;
        this.update();
        window.clearTimeout(this.settleT);
        this.settleT = window.setTimeout(() => this.settle(), 400);
      }, { passive: true });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') this.goTo(this.index + 1);
        if (e.key === 'ArrowLeft') this.goTo(this.index - 1);
      });
      document.addEventListener('visibilitychange', () => this.update());
      window.addEventListener('resize', () => {
        window.cancelAnimationFrame(this.resizeRaf);
        this.resizeRaf = window.requestAnimationFrame(() => this.jump(this.index));
      });

      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => {
          this.visible = entries[0].isIntersecting;
          this.update();
        }, { threshold: 0.25 });
        this.io.observe(this);
      }

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', (e) => {
          const i = this.slides.findIndex((s) => s.contains(e.target) || s === e.target);
          if (i > -1) { this.editorLocked = true; this.jump(i); this.setActive(i); this.update(true); }
        });
        document.addEventListener('shopify:block:deselect', () => { this.editorLocked = false; this.update(); });
      }

      this.jump(0);
      this.setActive(0);
      this.update(true);
    }

    disconnectedCallback() {
      this.stopTimer();
      if (this.io) this.io.disconnect();
    }

    /* Todo el slide lleva al enlace del botón principal, salvo que tenga un botón o enlace secundario.
       Un arrastre (deslizar) no cuenta como clic. */
    linkSlides() {
      this.slides.forEach((slide) => {
        const primary = slide.querySelector('.aro-hc__btn--primary[href]');
        const secondary = slide.querySelector('.aro-hc__btn--secondary, .aro-hc__link');
        if (!primary || secondary) return;
        slide.style.cursor = 'pointer';
        let x0 = 0, y0 = 0;
        slide.addEventListener('pointerdown', (e) => { x0 = e.clientX; y0 = e.clientY; });
        slide.addEventListener('click', (e) => {
          if (e.defaultPrevented || e.target.closest('a, button, input, select, textarea, video[controls]')) return;
          if (Math.abs(e.clientX - x0) > 8 || Math.abs(e.clientY - y0) > 8) return;
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) { window.open(primary.href, '_blank'); return; }
          window.location.href = primary.href;
        });
      });
    }

    /* Copia del último slide al inicio y del primero al final, para que el deslizado no tenga tope */
    addClones() {
      const make = (slide) => {
        const c = slide.cloneNode(true);
        c.removeAttribute('data-slide');
        c.removeAttribute('id');
        c.removeAttribute('data-shopify-editor-block');
        c.setAttribute('data-clone', '');
        c.setAttribute('aria-hidden', 'true');
        c.setAttribute('inert', '');
        c.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
        c.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
        c.querySelectorAll('video').forEach((v) => { v.removeAttribute('autoplay'); v.autoplay = false; v.pause(); });
        return c;
      };
      this.track.insertBefore(make(this.slides[this.n - 1]), this.slides[0]);
      this.track.appendChild(make(this.slides[0]));
      this.offset = 1;
    }

    /* Distancia entre el inicio de un slide y el siguiente (ancho + separación) */
    step() {
      if (this.n > 1) return (this.slides[1].offsetLeft - this.slides[0].offsetLeft) || this.track.clientWidth || 1;
      return this.track.clientWidth || 1;
    }

    rawIndex() { return Math.round(this.track.scrollLeft / this.step()); }

    /* Salto sin animación (se usa al pasar de una copia al slide real) */
    jump(i) {
      const prevBehavior = this.track.style.scrollBehavior;
      this.track.style.scrollBehavior = 'auto';
      this.track.scrollLeft = (i + this.offset) * this.step();
      this.track.style.scrollBehavior = prevBehavior;
    }

    onScroll() {
      window.cancelAnimationFrame(this.raf);
      this.raf = window.requestAnimationFrame(() => {
        if (this.scrollLock) return;
        const i = (((this.rawIndex() - this.offset) % this.n) + this.n) % this.n;
        if (i !== this.index) { this.setActive(i); this.update(true); }
      });
      /* Respaldo de 'scrollend' (no siempre se dispara): al quedarse quieto, acomoda */
      window.clearTimeout(this.settleT);
      this.settleT = window.setTimeout(() => this.settle(), 160);
    }

    /* Si el carrusel quedó sobre una copia, salta al slide real equivalente */
    settle() {
      if (!this.loop || this.touching) return;
      const raw = this.rawIndex();
      if (raw <= 0) this.jump(this.n - 1);
      else if (raw >= this.n + 1) this.jump(0);
    }

    goTo(i) {
      const n = this.n;
      const t = this.loop ? Math.max(-1, Math.min(n, i)) : ((i % n) + n) % n;
      this.scrollLock = true;
      window.clearTimeout(this.lockTimer);
      this.lockTimer = window.setTimeout(() => { this.scrollLock = false; this.settle(); }, 900);
      this.track.scrollTo({ left: (t + this.offset) * this.step(), behavior: this.reduced ? 'auto' : 'smooth' });
      this.setActive(((t % n) + n) % n);
      this.update(true);
    }

    setActive(i) {
      this.index = i;
      this.slides.forEach((slide, k) => {
        const active = k === i;
        slide.toggleAttribute('inert', !active);
        slide.querySelectorAll('video').forEach((v) => {
          if (active) { const p = v.play(); if (p && p.catch) p.catch(() => {}); } else { v.pause(); }
        });
      });
      this.dots.forEach((dot, k) => dot.setAttribute('aria-selected', k === i ? 'true' : 'false'));
    }

    canPlay() {
      return this.autoplay && !this.reduced && !this.focused && !this.touching && this.visible && !this.editorLocked && document.visibilityState === 'visible';
    }

    /* reset = slide nuevo: el tiempo y la barra empiezan de cero. Sin reset: pausa o continúa donde iba. */
    update(reset) {
      if (reset) {
        this.stopTimer();
        this.remaining = this.interval;
        this.classList.remove('is-playing');
        void this.offsetWidth;
        if (this.autoplay) this.classList.add('is-playing');
      }
      if (this.canPlay()) this.run(); else this.pause();
    }

    run() {
      if (this.timer) return;
      this.startedAt = performance.now();
      this.classList.add('is-playing');
      this.classList.remove('is-paused');
      this.timer = window.setTimeout(() => { this.timer = null; this.goTo(this.index + 1); }, this.remaining);
    }

    pause() {
      if (this.timer) {
        window.clearTimeout(this.timer);
        this.timer = null;
        this.remaining = Math.max(0, this.remaining - (performance.now() - this.startedAt));
      }
      if (this.autoplay) this.classList.add('is-paused');
    }

    stopTimer() {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
  }
  customElements.define('aro-hero-carousel', AroHeroCarousel);
}
